import { uuidv7 } from "uuidv7"

import type { Conversation } from "~/models/polyschema/Conversation"

import { parseConversation } from "~/utils/flatbuffers/conversation"

import type { ConversationBuf } from "../generated/conversation"

import { conversationMember, PropertySchema, PropertyStatus } from "../entity"
import { InterfaceSelection } from "../entity/InterfaceSelection"
import { withStatic } from "../entity/StaticEntity"
import { FileUserRole } from "../file/properties/FileUserRole"
import { DatagramErrorTypeBuf } from "../generated/datagram"
import { sendConversationPropertiesRequests } from "../handlers/conversation/properties"
import { sendConversationUpdateRequests } from "../handlers/conversation/update"
import { BaseConversation } from "./BaseConversation"

/**
 * A static conversation simply wraps a conversation object, allowing us to
 * easily export the native
 */
export class StaticConversation extends withStatic(BaseConversation) {
  _inner: Conversation

  constructor(conversation: Conversation) {
    super()
    this._inner = conversation
  }

  inner(): Readonly<Conversation> {
    return this._inner
  }

  static from(conversation?: Conversation) {
    if (conversation)
      return new StaticConversation(conversation)
  }

  static fromBuf(buf: ConversationBuf): StaticConversation {
    return new StaticConversation(parseConversation(buf))
  }

  /**
   * NOTE: ConversationEvent is just an alias for Conversation (for now)
   */
  static fromEvent(event: ConversationEvent): StaticConversation {
    return new StaticConversation(event)
  }

  /**
   * When we don't have enough info about a conversation to type it. Should only be used for, e.g.
   * moving conversations or other clear placeholder tasks. Should not be used to create a conversation
   * in the UI as this conversation will lack other necessary properties
   */
  static blank(id: string) {
    const selection = InterfaceSelection.nothing().inner()
    return new StaticConversation({ id, properties: {}, selection })
  }

  /**
   * Adds a pending owner property to this conversation
   */
  withSelfOwner(userId: string, timestamp: string) {
    this.addSelfOwner(userId, timestamp)
    return this
  }

  withCreatedAt(userId: string, timestamp: string) {
    this.addCreatedAt(userId, timestamp)
    return this
  }

  /**
   * FIXME: This style of writing causes there to be uncaught errors in promises because a
   * user that handles the authoritative and not cached promise won't catch the error.
   *
   * FIXME: use waitForFirstDataResponse
   */
  getProperties(selection?: InterfaceSelection) {
    const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
    const { isOffline, isConversationFresh } = useCache()

    const {
      resolve: cachedResolve,
      reject: cachedReject,
      promise: cached,
    } = Promise.withResolvers<StaticConversation>()
    const {
      resolve: authoritativeResolve,
      reject: authoritativeReject,
      promise: authoritative,
    } = Promise.withResolvers<StaticConversation>()
    // retrieve this conversation's properties and provide a cached and authoritative
    // promise to wait on either response
    const requestId = generateWorkerRequestId()

    const onProperties = (response: ConversationPropertiesResponseDatagram) => {
      if (response.offline && response.error && isOffline()) {
        cachedReject(response.error)
        authoritativeReject(response.error)
      }
      else if (response.offline && response.error) {
        // TODO: this cached rejection causes headaches in testing. We need to migrate to a
        // better error handling style, by, e.g. adding the error in the response type
        // rather than using the try/catch style which is pretty awful.
        // cachedReject(response.error)
        return
      }
      else if (
        response.offline
        && isDefined(response.conversation)
        && isConversationFresh(new StaticConversation(response.conversation))
      ) {
        cachedResolve(new StaticConversation(response.conversation))
        authoritativeResolve(new StaticConversation(response.conversation))
      }
      else if (response.offline && isDefined(response.conversation) && isOffline()) {
        cachedResolve(new StaticConversation(response.conversation))
        authoritativeReject({ details: "Failed to get conversation properties", type: DatagramErrorTypeBuf.CurrentlyOffline })
      }
      else if (response.offline && isDefined(response.conversation)) {
        cachedResolve(new StaticConversation(response.conversation))
        // not fresh, so we resolve the cache only
        return
      }
      else if (!response.offline && response.error) {
        cachedReject(response.error)
        authoritativeReject(response.error)
      }
      else if (!response.offline && isDefined(response.conversation)) {
        cachedResolve(new StaticConversation(response.conversation))
        authoritativeResolve(new StaticConversation(response.conversation))
      }
      else {
        console.error("Received conversation properties in unknown state", response)
        cachedReject({ type: DatagramErrorTypeBuf.InternalServerError, details: "Received conversation properties in unknown state" })
        authoritativeReject({ type: DatagramErrorTypeBuf.InternalServerError, details: "Received conversation properties in unknown state" })
      }
      removeInterfaceEventListener("conversationPropertiesResponse", onProperties)
    }

    addInterfaceEventListener(
      "conversationPropertiesResponse",
      onProperties,
      { requestId },
    )

    selection = selection ?? InterfaceSelection.minimumConversationViewSelection()

    sendConversationPropertiesRequests([{
      requestId,
      conversationId: this.id,
      selection: selection.inner(),
      offline: false,
    }])

    return { cached, authoritative }
  }

  async sendInvite(userId: string, remove = false, timeoutMs = 5000) {
    const { waitForFirstResponse } = useEvents()
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot invite user without a user")
    const conversation = StaticConversation.blank(this.inner().id)
    conversation.addProperty({
      id: this.inner().id,
      propertyId: conversationMember(userId),
      value: {
        type: PropertySchema.RoleUuidValue,
        role: FileUserRole.newEditor().inner(),
      },
      version: 0,
      deleted: remove,
      schema: PropertySchema.RoleUuidValue,
      author: myself.value.user.id,
      timestamp: uuidv7(),
      sharedWithUserId: userId,
      status: PropertyStatus.StatusPending,
    })
    const requestId = generateWorkerRequestId()
    sendConversationUpdateRequests([{
      requestId,
      conversation: conversation.inner(),
      offline: false,
    }])
    const response = await withTimeout(
      waitForFirstResponse("conversationUpdateResponse", requestId),
      timeoutMs,
    )
    if (response.error || !isDefined(response.conversation))
      throw new Error("User not found")
  }
}
